/**
 * The session's history: what happened in it, as text.
 *
 * This used to replay the recorded bytes into a second terminal emulator, and it
 * could not be made to work. tmux does not describe a session by printing its
 * text: it moves the cursor to a row and a column and writes there — 90,692
 * absolute cursor addressings against 5,388 line feeds in one operator's real
 * 3.8 MB record. Replayed, that gives back every intermediate state of every
 * line an agent was still streaming, and at any width but the one it was drawn
 * for, every address in the wrong place. The operator's word was "unreadable".
 *
 * Rendering it on the server instead gives an exact screen and, for a shell,
 * an exact transcript — but not for the CLIs that matter here. Claude Code and
 * Codex draw on the *alternate screen*, where nothing ever scrolls away, so
 * there is no line to record: measured on a live session, `history_size=0`
 * after 114 KB of output.
 *
 * What those CLIs do keep is their own record — every turn, tool call and result
 * as a line of JSON, in the account directory Kimene points them at. The server
 * follows it, renders it to text and serves it here. So this view is plain text,
 * deliberately: no emulator, no geometry, no replay — a scrolling list the
 * browser can search, select and copy like any other text on the page.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { useWindowedLines } from './useWindowedLines'
import clsx from 'clsx'
import { Button } from '@/components/primitives'
import { useT } from '@/i18n/context'

interface TranscriptWindow {
  sessionId: string
  /** The oldest line still held and the line after the newest. */
  first: number
  end: number
  /** What this window covers. */
  from: number
  next: number
  /** Lines that have left the server's memory. They are on disk, not here. */
  dropped: number
  lines: string[]
  /** The session's screen as it stands: not history, because it can still be
   *  redrawn. Carried separately for exactly that reason. */
  screen: string[]
}

/** One page of history. Lines, not bytes — the unit the record is kept in. */
const PAGE_LINES = 2000

export function TranscriptOverlay({
  sessionId,
  onClose,
  focusSearch = false,
}: {
  sessionId: string
  onClose: () => void
  /** Opened *to search* rather than to read. The only difference is which
   *  control the keyboard lands on, and it is worth the prop: the pane's menu
   *  offers both doors, and one of them promised a search box. */
  focusSearch?: boolean
}) {
  const t = useT()
  const [query, setQuery] = useState('')

  // The paging, the scroll anchoring and the error handling are the recording
  // overlay's too. See useWindowedLines for why that arithmetic is shared rather
  // than copied.
  const {
    window: win,
    lines,
    loading,
    error,
    hasOlder,
    loadOlder,
    scrollRef,
  } = useWindowedLines<TranscriptWindow>(`/sessions/${sessionId}/transcript`, PAGE_LINES)
  const matcher = query.trim().toLocaleLowerCase('tr')
  /** Which lines matched, in order, so the view can be walked through them.
   *  Indices rather than a count: counting told an operator there were seventeen
   *  and left them to scroll for each one. */
  const hits = useMemo(
    () =>
      matcher
        ? lines.reduce<number[]>((acc, line, i) => {
            if (line.toLocaleLowerCase('tr').includes(matcher)) acc.push(i)
            return acc
          }, [])
        : [],
    [lines, matcher],
  )
  const matches = hits.length
  const [at, setAt] = useState(0)

  // A new query starts at the top of its own results rather than wherever the
  // last one left off.
  useEffect(() => setAt(0), [matcher])

  /**
   * Moves to a match and scrolls it into view.
   *
   * Wraps, deliberately: the alternative is a control that stops working at the
   * end with nothing to say about why, and a transcript is read in both
   * directions.
   */
  const goToHit = useCallback(
    (delta: number) => {
      if (hits.length === 0) return
      const next = (at + delta + hits.length) % hits.length
      setAt(next)
      const el = scrollRef.current?.querySelectorAll('.k-hist__line')[hits[next]!]
      el?.scrollIntoView({ block: 'center' })
    },
    [at, hits],
  )

  return (
    <div className="k-hist" role="dialog" aria-modal="false" aria-label={t('terminal.history')}>
      <header className="k-hist__head">
        <div className="k-hist__ident">
          <span className="k-hist__title">{t('terminal.history')}</span>
          <span className="k-hist__note">{t('terminal.historyHint')}</span>
        </div>
        <div className="k-hist__actions">
          <input
            type="search"
            className="k-hist__search"
            placeholder={t('terminal.search')}
            aria-label={t('terminal.search')}
            autoFocus={focusSearch}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Escape') onClose()
              // The same vocabulary as the pane's own search, so an operator who
              // learns one has learned both.
              if (e.key === 'Enter') goToHit(e.shiftKey ? -1 : 1)
            }}
          />
          {matcher !== '' && (
            <span className="k-hist__busy k-mono">
              {matches === 0
                ? t('terminal.historyMatches', { matches })
                : t('terminal.searchAt', { at: at + 1, total: matches })}
            </span>
          )}
          {loading && win && (
            <span className="k-hist__busy" role="status">
              {t('state.loading')}
            </span>
          )}
          <Button
            size="sm"
            variant="ghost"
            disabled={!hasOlder || loading}
            onClick={() => void loadOlder()}
          >
            {t('terminal.historyOlder')}
          </Button>
          {/* Focused on open: the overlay covers a live pane, so the first
              thing the keyboard can do is leave. Unless it was opened *to
              search*, in which case the first thing to do is type — and two
              autofocused controls in one dialog is a race whose winner is
              whichever mounts last. */}
          <Button size="sm" variant="primary" onClick={onClose} autoFocus={!focusSearch}>
            {t('terminal.historyLive')}
          </Button>
        </div>
      </header>

      {/* The extent, in lines. The record has a first line and a last, and a
          count of what has left memory — a history view that silently began at
          the oldest line it happened to hold would read as complete. */}
      {win && (
        <div className="k-hist__extent">
          <div className="k-hist__marks">
            <span className="k-mono">{win.first}</span>
            <span className="k-mono k-hist__here">
              {t('terminal.historyLines', { from: win.from, to: win.next })}
            </span>
            <span className="k-mono">{win.end}</span>
          </div>
          {win.dropped > 0 && (
            <p className="k-hist__dropped" role="note">
              {t('terminal.historyDroppedLines', { lines: win.dropped })}
            </p>
          )}
        </div>
      )}

      {error && (
        <p className="k-hist__error" role="alert">
          {error}
        </p>
      )}

      <div className="k-hist__lines" ref={scrollRef} tabIndex={0}>
        {lines.map((line, i) => (
          <div
            key={win ? win.from + i : i}
            className={clsx(
              'k-hist__line',
              matcher !== '' &&
                line.toLocaleLowerCase('tr').includes(matcher) &&
                'k-hist__line--hit',
              // The one Enter is standing on, so "3/17" names something visible.
              hits[at] === i && 'k-hist__line--at',
            )}
          >
            {line === '' ? ' ' : line}
          </div>
        ))}
        {win && win.screen.length > 0 && (
          <>
            {/* The pane as it stands, rendered on the server. Shown because an
                operator reading to the end expects to arrive at the present, and
                marked as separate because it is a screen and can still be
                redrawn — presenting a half-drawn line as a recorded one is the
                thing this whole view was rebuilt to stop. */}
            <div className="k-hist__now">{t('terminal.historyNow')}</div>
            {win.screen.map((line, i) => (
              <div key={`screen-${i}`} className="k-hist__line k-hist__line--now">
                {line === '' ? ' ' : line}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  )
}
